import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCourses } from '../../hooks/useCourses';
import { useAdminCourses } from '../../hooks/useAdminCourses';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff, FiSearch, FiFilter } = FiIcons;

const CourseManagement = () => {
  const { courses, loading } = useCourses(true); // Include unpublished
  const { togglePublishStatus, deleteCourse } = useAdminCourses();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'published' && course.is_published) ||
                         (statusFilter === 'draft' && !course.is_published);
    return matchesSearch && matchesStatus;
  });

  const handleTogglePublish = async (courseId, currentStatus) => {
    await togglePublishStatus(courseId, !currentStatus);
  };

  const handleDeleteCourse = async (courseId) => {
    if (deleteConfirm === courseId) {
      await deleteCourse(courseId);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(courseId);
      setTimeout(() => setDeleteConfirm(null), 3000); // Auto-cancel after 3s
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Course Management</h1>
            <p className="text-gray-600 mt-2">Create and manage your course catalog</p>
          </div>
          <Link
            to="/admin/courses/new"
            className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create Course</span>
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
              >
                <option value="all">All Courses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>
        </div>

        {/* Course Grid */}
        {filteredCourses.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <SafeIcon icon={FiPlus} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || statusFilter !== 'all' ? 'No courses found' : 'No courses yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Create your first course to get started'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Link
                to="/admin/courses/new"
                className="inline-flex items-center space-x-2 bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Create Course</span>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {filteredCourses.map((course) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Course Image */}
                  <div className="relative h-48 bg-gradient-to-br from-nutrition-100 to-nutrition-200">
                    {course.cover_image_url ? (
                      <img
                        src={course.cover_image_url}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <SafeIcon icon={FiPlus} className="w-12 h-12 text-nutrition-600" />
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-4 left-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        course.is_published
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {course.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>

                    {/* Price Badge */}
                    {course.price > 0 && (
                      <div className="absolute top-4 right-4 bg-white rounded-full px-3 py-1 text-sm font-semibold text-gray-900">
                        ${course.price}
                      </div>
                    )}
                  </div>

                  {/* Course Content */}
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {course.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">{course.slug}</p>
                    {course.summary && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {course.summary}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleTogglePublish(course.id, course.is_published)}
                          className={`p-2 rounded-md transition-colors ${
                            course.is_published
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-gray-400 hover:bg-gray-50'
                          }`}
                          title={course.is_published ? 'Published' : 'Unpublished'}
                        >
                          <SafeIcon icon={course.is_published ? FiEye : FiEyeOff} className="w-4 h-4" />
                        </button>

                        <Link
                          to={`/admin/courses/${course.id}`}
                          className="p-2 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit Course"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </Link>

                        <button
                          onClick={() => handleDeleteCourse(course.id)}
                          className={`p-2 rounded-md transition-colors ${
                            deleteConfirm === course.id
                              ? 'text-red-600 bg-red-50'
                              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                          title={deleteConfirm === course.id ? 'Click again to confirm' : 'Delete Course'}
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>

                      <Link
                        to={`/courses/${course.slug}`}
                        className="text-nutrition-600 hover:text-nutrition-700 text-sm font-medium"
                      >
                        View →
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseManagement;